import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { acting } from "../lib/api";
import { support } from "../lib/supportApi";
import type { SupportSession } from "../lib/supportApi";
import type {
  Service,
  Project,
  Deployment,
  ManagedDatabase,
} from "../lib/types";
import { useApi } from "../lib/useApi";
import { useAction } from "./ProjectTools";
import { Button, CardSkeleton } from "../components/ui";

export default function OperatorSupport() {
  const { userId = "" } = useParams();
  const [reason, setReason] = useState(""),
    [session, setSession] = useState<SupportSession | null>(null);
  const { busy, run, feedback } = useAction(() => {});
  return (
    <>
      <header className="page-head">
        <div>
          <p className="platform-eyebrow">AUDITED OPERATOR ACCESS</p>
          <h1>Customer support session</h1>
          <p className="subtitle">
            Manage customer resources with your operator identity.
          </p>
        </div>
        <Link to={`/customers/${userId}`}>Customer account →</Link>
      </header>
      {feedback}
      {!session ? (
        <form
          className="card stack"
          onSubmit={(e) => {
            e.preventDefault();
            void run(
              async () =>
                {
                  const opened = await support.startSession(userId, reason);
                  acting.set(opened);
                  setSession(opened);
                },
              "Support session started",
            );
          }}
        >
          <label>
            Reason for access
            <textarea
              required
              minLength={10}
              maxLength={500}
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Describe the customer's issue and the support work you will perform."
            />
          </label>
          <Button loading={busy} type="submit">
            Open 30-minute support session
          </Button>
        </form>
      ) : (
        <>
          <div className="operator-support-banner" role="status">
            <strong>Supporting {session.customer_email}</strong>
            <span>
              Expires {new Date(session.expires_at).toLocaleTimeString()} · Your
              actions are audited
            </span>
            <Button
              disabled={busy}
              onClick={() =>
                void run(async () => {
                  await support.stopSession(session.id);
                  acting.set(null);
                  setSession(null);
                }, "Support session ended")
              }
            >
              End support session
            </Button>
          </div>
          <CustomerResources session={session} />
        </>
      )}
    </>
  );
}

function CustomerResources({ session }: { session: SupportSession }) {
  const services = useApi(
    () => support.resource<Service[]>(session.id, "/v1/services"),
    [session.id],
    15_000,
  );
  const projects = useApi(
    () => support.resource<Project[]>(session.id, "/v1/projects"),
    [session.id],
    15_000,
  );
  const databases = useApi(
    () => support.resource<ManagedDatabase[]>(session.id, "/v1/databases"),
    [session.id],
    15_000,
  );
  const [selected, setSelected] = useState("");
  const { busy, run, feedback } = useAction(() => {
    services.reload();
    projects.reload();
    databases.reload();
  });
  return (
    <div className="stack">
      {feedback}
      {[services.error, projects.error, databases.error]
        .filter(Boolean)
        .map((e, i) => (
          <p className="alert error" key={i}>
            {e}
          </p>
        ))}
      <section className="card">
        <h2>Application deployments</h2>
        {projects.data?.map((p) => (
          <div className="workspace-dependency" key={p.id}>
            <div>
              <strong>{p.name}</strong>
              <p className="tiny muted">
                {p.repo_full_name} · {p.branch}
              </p>
              {p.releases[0] && (
                <p className="tiny muted">
                  Latest release: {p.releases[0].status}
                </p>
              )}
            </div>
            <div className="row">
              <Button
                disabled={busy}
                onClick={() =>
                  void run(
                    () =>
                      support.resource(
                        session.id,
                        `/v1/projects/${p.id}/deploys`,
                        "POST",
                      ),
                    "Application deployment accepted",
                  )
                }
              >
                Deploy application
              </Button>
              {p.releases[0] && p.releases[0].status === "deploying" && (
                <>
                  <Button
                    disabled={busy}
                    onClick={() =>
                      void run(
                        () =>
                          support.resource(
                            session.id,
                            `/v1/projects/${p.id}/deploys/${p.releases[0].id}/cancel`,
                            "POST",
                          ),
                        "Remaining builds canceled",
                      )
                    }
                  >
                    Cancel remaining builds
                  </Button>
                  <Button
                    variant="danger"
                    disabled={busy}
                    onClick={() =>
                      void run(
                        () =>
                          support.resource(
                            session.id,
                            `/v1/projects/${p.id}/deploys?force=true`,
                            "POST",
                          ),
                        "Release restarted, superseding the one in flight",
                      )
                    }
                  >
                    Force new release
                  </Button>
                  {/* The customer is told to wait for the health check, and
                      that is right for them. An operator is here because it
                      is not going to pass. */}
                  <Button
                    variant="danger"
                    disabled={busy}
                    onClick={() => {
                      if (
                        !confirm(
                          `Stop this release wherever it has got to?\n\nComponents already rolling out are left part-applied — some pods on the new version, some on the old. Put it right with a rollback or a fresh deploy afterwards. This is recorded against you.`,
                        )
                      )
                        return
                      void run(
                        () =>
                          support.resource(
                            session.id,
                            `/v1/projects/${p.id}/deploys/${p.releases[0].id}/cancel?force=true`,
                            "POST",
                          ),
                        "Release stopped mid-rollout",
                      )
                    }}
                  >
                    Force stop release
                  </Button>
                </>
              )}
              {p.releases[0] &&
                ["failed", "canceled", "superseded"].includes(
                  p.releases[0].status,
                ) && (
                  <Button
                    disabled={busy}
                    onClick={() =>
                      void run(
                        () =>
                          support.resource(
                            session.id,
                            `/v1/projects/${p.id}/deploys/${p.releases[0].id}/retry`,
                            "POST",
                          ),
                        "Retry accepted for the recorded commit",
                      )
                    }
                  >
                    Retry release
                  </Button>
                )}
            </div>
          </div>
        ))}
      </section>
      <section className="card stack">
        <h2>Service configuration</h2>
        <label>
          Customer service
          <select
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
          >
            <option value="">Select a service</option>
            {services.data?.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} · {s.status}
              </option>
            ))}
          </select>
        </label>
        {selected && (
          <ServiceEditor
            key={selected}
            session={session.id}
            service={services.data!.find((s) => s.id === selected)!}
          />
        )}
      </section>
      <AnyRequest session={session.id} />
      <section className="card">
        <h2>Managed databases</h2>
        {databases.data?.map((d) => (
          <div className="workspace-dependency" key={d.id}>
            <div>
              <strong>{d.name}</strong>
              <p className="tiny muted">
                {d.engine} · {d.status} · {d.plan}
              </p>
            </div>
            {d.status === "failed" && (
              <Button
                disabled={busy}
                onClick={() =>
                  void run(
                    () =>
                      support.resource(
                        session.id,
                        `/v1/databases/${d.id}/retry`,
                        "POST",
                      ),
                    "Database recovery requested",
                  )
                }
              >
                Retry recovery
              </Button>
            )}
            <Button
              disabled={busy || d.status !== "available"}
              onClick={() =>
                void run(
                  () =>
                    support.resource(
                      session.id,
                      `/v1/databases/${d.id}/backups`,
                      "POST",
                    ),
                  "Backup request completed",
                )
              }
            >
              Take backup
            </Button>
          </div>
        ))}
      </section>
    </div>
  );
}

function ServiceEditor({
  session,
  service,
}: {
  session: string;
  service: Service;
}) {
  const [patch, setPatch] = useState(
    JSON.stringify(
      {
        build_command: service.build_command,
        start_command: service.start_command,
        root_directory: service.root_directory,
        port: service.port,
        health_check_path: service.health_check_path,
      },
      null,
      2,
    ),
  );
  const [key, setKey] = useState(""),
    [value, setValue] = useState("");
  const deployments = useApi(
    () =>
      support.resource<Deployment[]>(
        session,
        `/v1/services/${service.id}/deploys`,
      ),
    [session, service.id],
    15_000,
  );
  const [result, setResult] = useState("");
  const { busy, run, feedback } = useAction(deployments.reload);
  return (
    <div className="stack">
      {feedback}
      <p className="muted">
        Edit settings using the same validation as the customer dashboard.
        Changes may redeploy this service. Repository source changes require a
        GitHub pull request.
      </p>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          void run(async () => {
            const body: unknown = JSON.parse(patch);
            if (!body || Array.isArray(body) || typeof body !== "object")
              throw new Error("Enter a JSON object");
            await support.resource(
              session,
              `/v1/services/${service.id}`,
              "PATCH",
              body,
            );
          }, "Service configuration saved");
        }}
      >
        <label>
          Configuration patch
          <textarea
            className="advanced-code"
            rows={9}
            value={patch}
            onChange={(e) => setPatch(e.target.value)}
          />
        </label>
        <Button loading={busy} type="submit">
          Apply configuration patch
        </Button>
      </form>
      <form
        className="advanced-form"
        onSubmit={(e) => {
          e.preventDefault();
          void run(async () => {
            await support.resource(
              session,
              `/v1/services/${service.id}/env`,
              "PUT",
              {
                variables: [{ key, value, is_secret: true, build_time: false }],
              },
            );
            setValue("");
          }, "Environment updated");
        }}
      >
        <label>
          Variable name
          <input
            required
            value={key}
            onChange={(e) => setKey(e.target.value)}
          />
        </label>
        <label>
          Secret value
          <input
            type="password"
            autoComplete="off"
            required
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
        </label>
        <Button loading={busy} type="submit">
          Save variable
        </Button>
      </form>
      <div className="row">
        <Button
          disabled={busy}
          onClick={() =>
            void run(
              () =>
                support.resource(
                  session,
                  `/v1/services/${service.id}/deploy-application`,
                  "POST",
                ),
              "Application deployment accepted",
            )
          }
        >
          Deploy application
        </Button>
        <Button
          disabled={busy}
          onClick={() =>
            void run(
              () =>
                support.resource(
                  session,
                  `/v1/services/${service.id}/deploys`,
                  "POST",
                  { commit_sha: null },
                ),
              "Deployment started for this service",
            )
          }
        >
          Deploy this service
        </Button>
        <Button
          disabled={busy || service.status !== "live"}
          onClick={() =>
            void run(
              () =>
                support.resource(
                  session,
                  `/v1/services/${service.id}/restart`,
                  "POST",
                ),
              "Restarting",
            )
          }
        >
          Restart
        </Button>
        <Button
          disabled={busy}
          onClick={() =>
            void run(
              () =>
                support.resource(
                  session,
                  `/v1/services/${service.id}/${
                    service.status === "suspended" ? "resume" : "suspend"
                  }`,
                  "POST",
                ),
              service.status === "suspended" ? "Resuming" : "Suspended",
            )
          }
        >
          {service.status === "suspended" ? "Resume" : "Suspend"}
        </Button>
        <Button
          disabled={busy}
          onClick={() =>
            void run(
              async () =>
                setResult(
                  JSON.stringify(
                    await support.resource(
                      session,
                      `/v1/services/${service.id}`,
                    ),
                    null,
                    2,
                  ),
                ),
              "Service details loaded",
            )
          }
        >
          Inspect service
        </Button>
      </div>
      {result && (
        <details open>
          <summary>Service details</summary>
          <pre className="advanced-code">{result}</pre>
        </details>
      )}
      <h3>Recent deployments</h3>
      {deployments.error && <p className="alert error">{deployments.error}</p>}
      {!deployments.data && !deployments.error && <CardSkeleton />}
      {deployments.data?.slice(0, 10).map((d) => (
        <div className="workspace-dependency" key={d.id}>
          <div>
            <strong>
              {d.commit_sha.slice(0, 8)} · {d.status}
            </strong>
            {d.error_message && (
              <p className="platform-failure">{d.error_message}</p>
            )}
          </div>
          <div className="row">
            {["queued", "cloning", "building", "pushing"].includes(d.status) && (
              <Button
                disabled={busy}
                onClick={() =>
                  void run(
                    () =>
                      support.resource(
                        session,
                        `/v1/deploys/${d.id}/cancel`,
                        "POST",
                      ),
                    "Deployment cancellation requested",
                  )
                }
              >
                Cancel build
              </Button>
            )}
            {d.status === "deploying" && (
              <Button
                variant="danger"
                disabled={busy}
                onClick={() => {
                  if (
                    !confirm(
                      `Stop this component while it is rolling out?\n\nIt is left part-applied — some pods on the new version, some on the old — and needs a rollback or a fresh deploy afterwards. This is recorded against you.`,
                    )
                  )
                    return
                  void run(
                    () =>
                      support.resource(
                        session,
                        `/v1/deploys/${d.id}/cancel?force=true`,
                        "POST",
                      ),
                    "Release stopped mid-rollout",
                  )
                }}
              >
                Force cancel
              </Button>
            )}
            {d.status === "live" && (
              <Button
                disabled={busy}
                onClick={() =>
                  void run(
                    () =>
                      support.resource(
                        session,
                        `/v1/deploys/${d.id}/rollback`,
                        "POST",
                      ),
                    "Rolling back to this deployment",
                  )
                }
              >
                Roll back to this
              </Button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

/** Everything the buttons above do not cover.
 *
 * The buttons are what an operator reaches for daily. This is the rest of the
 * customer's API, and it is here because the alternative is a console that
 * grows one button per endpoint and is still missing the one somebody needs
 * at two in the morning. It goes through the same support session, so it is
 * the customer's own endpoints with the customer's own validation, and every
 * call is audited against the operator exactly as the buttons are.
 */
function AnyRequest({ session }: { session: string }) {
  const [method, setMethod] = useState("GET");
  const [path, setPath] = useState("/v1/services");
  const [body, setBody] = useState("");
  const [result, setResult] = useState("");
  const { busy, run, feedback } = useAction(() => {});
  return (
    <section className="card stack">
      <h2>Any customer request</h2>
      <p className="muted">
        The customer's own API, as the customer. Paths under{" "}
        <span className="mono">/v1/services</span>,{" "}
        <span className="mono">/v1/projects</span>,{" "}
        <span className="mono">/v1/deploys</span>,{" "}
        <span className="mono">/v1/databases</span>,{" "}
        <span className="mono">/v1/blueprints</span> and{" "}
        <span className="mono">/v1/workspaces</span>. Add{" "}
        <span className="mono">?force=true</span> to a deploy or a cancel to
        take it at any stage, including one already rolling out.
      </p>
      {feedback}
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          void run(async () => {
            const parsed: unknown = body.trim() ? JSON.parse(body) : undefined;
            const response = await support.resource<unknown>(
              session,
              path,
              method,
              parsed,
            );
            setResult(JSON.stringify(response ?? { ok: true }, null, 2));
          }, "Request sent");
        }}
      >
        <div className="field-row">
          <label>
            Method
            <select value={method} onChange={(e) => setMethod(e.target.value)}>
              {["GET", "POST", "PATCH", "PUT", "DELETE"].map((m) => (
                <option key={m}>{m}</option>
              ))}
            </select>
          </label>
          <label style={{ flex: 1 }}>
            Path
            <input
              className="mono"
              required
              value={path}
              onChange={(e) => setPath(e.target.value)}
            />
          </label>
        </div>
        <label>
          JSON body (optional)
          <textarea
            className="advanced-code"
            rows={4}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder='{"plan": "standard"}'
          />
        </label>
        <Button loading={busy} type="submit">
          Send as the customer
        </Button>
      </form>
      {result && (
        <details open>
          <summary>Response</summary>
          <pre className="advanced-code">{result}</pre>
        </details>
      )}
    </section>
  );
}
